import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  createUserWithEmailAndPassword,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';

// Import Firebase instances from @platform/core
import { app, auth, db } from '@platform/core';

// Re-export for components that import from this file
export { app, auth, db };

const googleProvider = new GoogleAuthProvider();
const microsoftProvider = new OAuthProvider('microsoft.com');

// Types
export type UserRole = 'admin' | 'firm_lead' | 'consultant' | 'org_admin' | 'org_user' | 'viewer';

export interface Organization {
  id: string;
  name: string;
  logo?: string;  // URL to company logo image
  emoji?: string; // Emoji to represent the company (e.g., '🏢', '🏗️')
  language?: 'he' | 'en' | 'ar'; // Default language for org board content
  companyVfp?: string; // Initial company VFP (used when creating orgboard, then stored there)
  firmId?: string | null; // Which consulting firm owns this client org
  useFirmBranding?: boolean; // Whether to use firm's branding
  createdAt: Timestamp;
  createdBy: string;
  updatedAt?: Timestamp;
}

export interface ConsultingFirm {
  id: string;
  name: string;
  branding: {
    logo?: string;
    firmName: string;
  };
  leadUserId: string | null;
  leadEmail: string;
  isActive: boolean;
  createdAt: Timestamp;
  createdBy: string;
  updatedAt: Timestamp;
}

// Pre-defined company logos
export const COMPANY_LOGOS = {
  FEDERMAN_CONSULTANTS: 'https://turnkeyos.ai/turnkeyos-logo.svg',
} as const;

export interface AppUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  organizationId: string | null; // null for platform admin
  firmId?: string | null; // Consulting firm membership
  assignedClientOrgIds?: string[]; // For consultants: assigned client orgs
  createdAt: Timestamp;
}

interface AuthContextType {
  // Auth state
  firebaseUser: FirebaseUser | null;
  user: AppUser | null;
  organization: Organization | null;
  organizations: Organization[]; // All available organizations (for dropdown)
  firm: ConsultingFirm | null; // User's consulting firm (if firm_lead or consultant)
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isFirmLead: boolean;
  isConsultant: boolean;
  isOrgAdmin: boolean;
  canEdit: boolean;

  // Auth actions
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithMicrosoft: () => Promise<void>;
  signUpWithEmail: (email: string, password: string, name: string, firmName?: string) => Promise<void>;
  logout: () => Promise<void>;

  // Admin actions (platform admin only)
  createOrganization: (name: string, options?: { language?: 'he' | 'en' | 'ar'; emoji?: string; logo?: string; companyVfp?: string }) => Promise<Organization>;
  getOrganizations: () => Promise<Organization[]>;
  deleteOrganization: (orgId: string) => Promise<void>;
  loadOrganizations: () => Promise<void>; // Refresh organizations list

  // User management (platform admin or org admin)
  createUser: (email: string, name: string, role: UserRole, organizationId: string) => Promise<AppUser>;
  getUsers: (organizationId?: string) => Promise<AppUser[]>;
  getPendingUsers: (organizationId?: string) => Promise<AppUser[]>;
  updateUserRole: (userId: string, role: UserRole) => Promise<void>;
  deleteUser: (userId: string) => Promise<void>;
  deletePendingUser: (userId: string) => Promise<void>;

  // Organization selection (for admin)
  selectOrganization: (orgId: string) => Promise<void>;
  updateOrganizationName: (name: string) => Promise<void>;
  updateOrganizationSettings: (settings: { logo?: string; emoji?: string; language?: 'he' | 'en' | 'ar'; name?: string }, orgId?: string) => Promise<void>;

  // Clone organization
  cloneOrganization: (orgId: string, newName: string, includeEmployees: boolean, firmId?: string) => Promise<Organization>;

  // Firm access helpers
  canAccessOrg: (orgId: string) => boolean;
  getAccessibleOrganizations: () => Organization[];
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [user, setUser] = useState<AppUser | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]); // All available orgs
  const [firm, setFirm] = useState<ConsultingFirm | null>(null); // User's consulting firm
  const [isLoading, setIsLoading] = useState(true);

  // Derived state
  const isAuthenticated = !!user;
  const isAdmin = user?.role === 'admin';
  const isFirmLead = user?.role === 'firm_lead';
  const isConsultant = user?.role === 'consultant';
  const isOrgAdmin = user?.role === 'org_admin' || isAdmin;
  const canEdit = user?.role !== 'viewer';

  // Helper to check if user can access a specific organization
  const canAccessOrg = useCallback((orgId: string): boolean => {
    if (!user) return false;
    if (isAdmin) return true;
    if (user.organizationId === orgId) return true;
    if (isConsultant && user.assignedClientOrgIds?.includes(orgId)) return true;
    if (isFirmLead && firm) {
      // Firm lead can access any org that belongs to their firm
      const org = organizations.find(o => o.id === orgId);
      return org?.firmId === firm.id;
    }
    return false;
  }, [user, isAdmin, isConsultant, isFirmLead, firm, organizations]);

  // Get all organizations the user can access
  const getAccessibleOrganizations = useCallback((): Organization[] => {
    if (!user) return [];
    if (isAdmin) return organizations;
    if (isFirmLead && firm) {
      return organizations.filter(org => org.firmId === firm.id);
    }
    if (isConsultant && user.assignedClientOrgIds) {
      return organizations.filter(org => user.assignedClientOrgIds?.includes(org.id));
    }
    if (user.organizationId) {
      return organizations.filter(org => org.id === user.organizationId);
    }
    return [];
  }, [user, isAdmin, isFirmLead, isConsultant, firm, organizations]);

  // Load user data from Firestore
  const loadUserData = useCallback(async (fbUser: FirebaseUser) => {
    try {
      const userDocRef = doc(db, 'users', fbUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = { id: userDoc.id, ...userDoc.data() } as AppUser;
        setUser(userData);

        // Load organization if user belongs to one
        if (userData.organizationId) {
          const orgDocRef = doc(db, 'organizations', userData.organizationId);
          const orgDoc = await getDoc(orgDocRef);
          if (orgDoc.exists()) {
            setOrganization({ id: orgDoc.id, ...orgDoc.data() } as Organization);
          }
        }

        // Load consulting firm if user belongs to one
        if (userData.firmId) {
          const firmDocRef = doc(db, 'consultingFirms', userData.firmId);
          const firmDoc = await getDoc(firmDocRef);
          if (firmDoc.exists()) {
            setFirm({ id: firmDoc.id, ...firmDoc.data() } as ConsultingFirm);
          }
        }
      } else {
        // First time user - create user record
        // Check if there's a pending user invitation for this email
        const pendingQuery = query(
          collection(db, 'pending_users'),
          where('email', '==', fbUser.email)
        );
        const pendingSnapshot = await getDocs(pendingQuery);

        let newUser: Omit<AppUser, 'id'>;

        if (!pendingSnapshot.empty) {
          // Found pending invitation - use the assigned role and org/firm
          const pendingData = pendingSnapshot.docs[0].data();
          newUser = {
            email: fbUser.email || '',
            name: pendingData.name || fbUser.displayName || fbUser.email?.split('@')[0] || 'User',
            role: pendingData.role,
            organizationId: pendingData.organizationId || null,
            firmId: pendingData.firmId || null,
            createdAt: Timestamp.now(),
          };

          // Delete the pending user record
          await deleteDoc(pendingSnapshot.docs[0].ref);
        } else {
          // No pending invitation - check if this is the first user (make them admin)
          const usersQuery = query(collection(db, 'users'));
          const usersSnapshot = await getDocs(usersQuery);
          const isFirstUser = usersSnapshot.empty;

          newUser = {
            email: fbUser.email || '',
            name: fbUser.displayName || fbUser.email?.split('@')[0] || 'User',
            role: isFirstUser ? 'admin' : 'viewer', // First user becomes admin
            organizationId: null,
            createdAt: Timestamp.now(),
          };
        }

        await setDoc(userDocRef, newUser);
        setUser({ id: fbUser.uid, ...newUser });

        // Load organization if assigned
        if (newUser.organizationId) {
          const orgDocRef = doc(db, 'organizations', newUser.organizationId);
          const orgDoc = await getDoc(orgDocRef);
          if (orgDoc.exists()) {
            setOrganization({ id: orgDoc.id, ...orgDoc.data() } as Organization);
          }
        }

        // Load firm if assigned (for firm leads and consultants)
        if (newUser.firmId) {
          const firmDocRef = doc(db, 'consultingFirms', newUser.firmId);
          const firmDoc = await getDoc(firmDocRef);
          if (firmDoc.exists()) {
            setFirm({ id: firmDoc.id, ...firmDoc.data() } as ConsultingFirm);
          }
        }
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  }, []);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);

      if (fbUser) {
        await loadUserData(fbUser);
      } else {
        setUser(null);
        setOrganization(null);
        setOrganizations([]);
        setFirm(null);
      }

      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [loadUserData]);

  // Load organizations into state (for dropdown)
  const loadOrganizations = useCallback(async (): Promise<void> => {
    // Admin sees all orgs
    if (isAdmin) {
      try {
        const orgsSnapshot = await getDocs(collection(db, 'organizations'));
        const orgs = orgsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Organization));
        setOrganizations(orgs);
      } catch (error) {
        console.error('Error loading organizations:', error);
        setOrganizations([]);
      }
      return;
    }

    // Firm lead sees all orgs in their firm
    if (isFirmLead && user?.firmId) {
      try {
        const orgsQuery = query(
          collection(db, 'organizations'),
          where('firmId', '==', user.firmId)
        );
        const orgsSnapshot = await getDocs(orgsQuery);
        const orgs = orgsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Organization));
        setOrganizations(orgs);
      } catch (error) {
        console.error('Error loading firm organizations:', error);
        setOrganizations([]);
      }
      return;
    }

    // Consultant sees only assigned orgs
    if (isConsultant && user?.assignedClientOrgIds && user.assignedClientOrgIds.length > 0) {
      try {
        // Firestore 'in' query supports up to 30 items
        const orgIds = user.assignedClientOrgIds.slice(0, 30);
        const orgsQuery = query(
          collection(db, 'organizations'),
          where('__name__', 'in', orgIds)
        );
        const orgsSnapshot = await getDocs(orgsQuery);
        const orgs = orgsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Organization));
        setOrganizations(orgs);
      } catch (error) {
        console.error('Error loading consultant organizations:', error);
        setOrganizations([]);
      }
      return;
    }

    setOrganizations([]);
  }, [isAdmin, isFirmLead, isConsultant, user?.firmId, user?.assignedClientOrgIds]);

  // Load organizations when user has multi-org access (admin, firm lead, or consultant)
  useEffect(() => {
    if (isAdmin || isFirmLead || isConsultant) {
      loadOrganizations();
    }
  }, [isAdmin, isFirmLead, isConsultant, loadOrganizations]);

  // Auth actions
  const signInWithEmail = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    setIsLoading(true);
    try {
      await signInWithPopup(auth, googleProvider);
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithMicrosoft = async () => {
    setIsLoading(true);
    try {
      await signInWithPopup(auth, microsoftProvider);
    } finally {
      setIsLoading(false);
    }
  };

  const signUpWithEmail = async (email: string, password: string, name: string, firmName?: string) => {
    setIsLoading(true);
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const userDocRef = doc(db, 'users', result.user.uid);

      if (firmName) {
        // Self-registration as consulting firm owner
        const now = Timestamp.now();
        const trialEndDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
        const trialEnd = Timestamp.fromDate(trialEndDate);

        // Create consulting firm
        const firmRef = await addDoc(collection(db, 'consultingFirms'), {
          name: firmName,
          branding: {
            firmName: firmName,
          },
          leadUserId: result.user.uid,
          leadEmail: email,
          isActive: true,
          trialStartDate: now,
          trialEndDate: trialEnd,
          createdAt: serverTimestamp(),
          createdBy: result.user.uid,
          updatedAt: serverTimestamp(),
        });

        // Create firm-level subscription with 30-day trial
        await setDoc(doc(db, 'subscriptions', firmRef.id), {
          organizationId: firmRef.id, // firm acts as the billing entity
          status: 'trialing',
          plan: 'starter',
          billingInterval: 'monthly',
          trialStartDate: now,
          trialEndDate: trialEnd,
          maxUsers: 5,
          maxCompanies: 3,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        // Create user as firm_lead
        const newUser: Omit<AppUser, 'id'> = {
          email,
          name,
          role: 'firm_lead',
          organizationId: null,
          firmId: firmRef.id,
          createdAt: Timestamp.now(),
        };
        await setDoc(userDocRef, newUser);
      } else {
        // Legacy signup (fallback — viewer role)
        const usersQuery = query(collection(db, 'users'));
        const usersSnapshot = await getDocs(usersQuery);
        const isFirstUser = usersSnapshot.empty;

        const newUser: Omit<AppUser, 'id'> = {
          email,
          name,
          role: isFirstUser ? 'admin' : 'viewer',
          organizationId: null,
          createdAt: Timestamp.now(),
        };
        await setDoc(userDocRef, newUser);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
    setOrganization(null);
    setFirm(null);
    setOrganizations([]);
  };

  // Admin actions - Create organization
  const createOrganization = async (name: string, options?: { language?: 'he' | 'en' | 'ar'; emoji?: string; logo?: string; companyVfp?: string }): Promise<Organization> => {
    if (!isAdmin) throw new Error('Only admins can create organizations');

    const { language = 'he', emoji, logo, companyVfp } = options || {};

    const orgData: any = {
      name,
      language,
      createdAt: serverTimestamp(),
      createdBy: user!.id,
      updatedAt: serverTimestamp(),
    };

    // Add optional fields if provided
    if (emoji) orgData.emoji = emoji;
    if (logo) orgData.logo = logo;
    if (companyVfp) orgData.companyVfp = companyVfp; // Will be used when creating orgboard

    const orgRef = await addDoc(collection(db, 'organizations'), orgData);

    const orgDoc = await getDoc(orgRef);
    return { id: orgDoc.id, ...orgDoc.data() } as Organization;
  };

  // Admin actions - Get all organizations
  const getOrganizations = async (): Promise<Organization[]> => {
    if (!isAdmin) throw new Error('Only admins can list organizations');

    const orgsSnapshot = await getDocs(collection(db, 'organizations'));
    return orgsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Organization));
  };

  // Admin actions - Delete organization
  const deleteOrganization = async (orgId: string): Promise<void> => {
    // Allow admins or firm leads (for their firm's orgs)
    if (!isAdmin && !isFirmLead) throw new Error('Only admins or firm leads can delete organizations');

    // For firm leads, verify the org belongs to their firm
    if (isFirmLead && !isAdmin) {
      const orgDoc = await getDoc(doc(db, 'organizations', orgId));
      if (!orgDoc.exists()) throw new Error('Organization not found');
      const orgData = orgDoc.data();
      if (orgData.firmId !== firm?.id) {
        throw new Error('You can only delete organizations belonging to your firm');
      }
    }

    // Delete all users in this organization
    const usersQuery = query(collection(db, 'users'), where('organizationId', '==', orgId));
    const usersSnapshot = await getDocs(usersQuery);
    for (const userDoc of usersSnapshot.docs) {
      await deleteDoc(userDoc.ref);
    }

    // Delete the orgboard for this organization (document ID is orgId)
    const orgboardRef = doc(db, 'orgboards', orgId);
    const orgboardSnap = await getDoc(orgboardRef);
    if (orgboardSnap.exists()) {
      await deleteDoc(orgboardRef);
    }

    // Delete the organization
    await deleteDoc(doc(db, 'organizations', orgId));
  };

  // Helper function to recursively remove undefined values from an object (Firestore doesn't accept undefined)
  const removeUndefined = (obj: any): any => {
    if (obj === null || obj === undefined) return null;
    if (Array.isArray(obj)) {
      return obj.map(item => removeUndefined(item));
    }
    if (typeof obj === 'object') {
      const cleaned: any = {};
      for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined) {
          cleaned[key] = removeUndefined(value);
        }
      }
      return cleaned;
    }
    return obj;
  };

  // Admin actions - Clone organization
  const cloneOrganization = async (orgId: string, newName: string, includeEmployees: boolean, firmId?: string): Promise<Organization> => {
    console.log('cloneOrganization called:', { orgId, newName, includeEmployees, firmId });

    // Allow admins or firm leads to clone
    if (!isAdmin && !isFirmLead) throw new Error('Only admins or firm leads can clone organizations');

    // Create new organization with optional firmId
    console.log('Creating new organization...');
    const orgData: any = {
      name: newName,
      createdAt: serverTimestamp(),
      createdBy: user!.id,
      updatedAt: serverTimestamp(),
    };
    if (firmId) {
      orgData.firmId = firmId;
    }
    const newOrgRef = await addDoc(collection(db, 'organizations'), orgData);

    const newOrgId = newOrgRef.id;
    console.log('New organization created with ID:', newOrgId);

    // Get original orgboard data - document ID is the organizationId
    console.log('Fetching original orgboard for:', orgId);
    const orgboardDocRef = doc(db, 'orgboards', orgId);
    const orgboardDocSnap = await getDoc(orgboardDocRef);
    console.log('Orgboard exists:', orgboardDocSnap.exists());

    if (orgboardDocSnap.exists()) {
      const originalOrgboard = orgboardDocSnap.data();
      console.log('Original orgboard keys:', Object.keys(originalOrgboard));
      const originalData = originalOrgboard.data; // The actual org data is in 'data' field
      console.log('Original data exists:', !!originalData);

      if (!originalData) {
        console.error('No data found in orgboard document');
      } else {
        // Create employee ID mapping if including employees
        const employeeIdMap: Record<string, string> = {};

        if (includeEmployees && originalData.employees) {
          // Generate new IDs for all employees
          for (const emp of originalData.employees) {
            const newId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            employeeIdMap[emp.id] = newId;
          }
        }

        // Deep clone the org data
        const clonedData: any = JSON.parse(JSON.stringify(originalData));

        if (includeEmployees && originalData.employees) {
          // Clone employees with new IDs
          clonedData.employees = originalData.employees.map((emp: any) => ({
            ...emp,
            id: employeeIdMap[emp.id],
            // Update manager reference if exists
            managerId: emp.managerId ? employeeIdMap[emp.managerId] || emp.managerId : undefined,
          }));

          // Update assignments with new employee IDs
          if (originalData.assignments) {
            clonedData.assignments = {};
            for (const [funcId, empIds] of Object.entries(originalData.assignments)) {
              clonedData.assignments[funcId] = (empIds as string[]).map(
                (empId: string) => employeeIdMap[empId] || empId
              );
            }
          }

          // Update division manager IDs
          if (clonedData.divisions) {
            for (const divKey of Object.keys(clonedData.divisions)) {
              const div = clonedData.divisions[divKey];
              if (div.managerId) {
                div.managerId = employeeIdMap[div.managerId] || div.managerId;
              }
              // Update department manager IDs
              if (div.departments) {
                for (const dept of div.departments) {
                  if (dept.managerId) {
                    dept.managerId = employeeIdMap[dept.managerId] || dept.managerId;
                  }
                  // Update section manager IDs
                  if (dept.sections) {
                    for (const section of dept.sections) {
                      if (section.managerId) {
                        section.managerId = employeeIdMap[section.managerId] || section.managerId;
                      }
                    }
                  }
                }
              }
            }
          }
        } else {
          // Without employees - clear all employee-related data
          clonedData.employees = [];
          clonedData.assignments = {};

          // Clear manager IDs from divisions
          if (clonedData.divisions) {
            for (const divKey of Object.keys(clonedData.divisions)) {
              const div = clonedData.divisions[divKey];
              div.managerId = undefined;
              if (div.departments) {
                for (const dept of div.departments) {
                  dept.managerId = undefined;
                  if (dept.sections) {
                    for (const section of dept.sections) {
                      section.managerId = undefined;
                    }
                  }
                }
              }
            }
          }

          // Clear executive function assignments
          if (clonedData.executiveFunctions) {
            clonedData.executiveFunctions = clonedData.executiveFunctions.map((ef: any) => ({
              ...ef,
            }));
          }
        }

        // Save cloned orgboard with new organization ID as document ID
        // Remove undefined values before saving (Firestore doesn't accept undefined)
        const cleanedData = removeUndefined(clonedData);
        const newOrgboardRef = doc(db, 'orgboards', newOrgId);
        await setDoc(newOrgboardRef, {
          organizationId: newOrgId,
          data: cleanedData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          updatedBy: user!.id,
        });
      }
    }

    const newOrgDoc = await getDoc(newOrgRef);
    return { id: newOrgDoc.id, ...newOrgDoc.data() } as Organization;
  };

  // User management - Create user
  const createUser = async (email: string, name: string, role: UserRole, organizationId: string): Promise<AppUser> => {
    if (!isAdmin && !isOrgAdmin) throw new Error('Only admins can create users');
    if (!isAdmin && role === 'admin') throw new Error('Only platform admins can create admin users');

    // For org_admin, they can only create users in their own organization
    if (!isAdmin && user?.organizationId !== organizationId) {
      throw new Error('You can only create users in your own organization');
    }

    // Create a placeholder user document (user will need to sign up with this email)
    const userRef = await addDoc(collection(db, 'pending_users'), {
      email,
      name,
      role,
      organizationId,
      createdAt: serverTimestamp(),
      createdBy: user!.id,
    });

    return {
      id: userRef.id,
      email,
      name,
      role,
      organizationId,
      createdAt: Timestamp.now(),
    };
  };

  // User management - Get users
  const getUsers = async (organizationId?: string): Promise<AppUser[]> => {
    let usersQuery;

    if (isAdmin && !organizationId) {
      // Admin can see all users
      usersQuery = query(collection(db, 'users'));
    } else {
      // Org admin can only see users in their organization
      const orgId = organizationId || user?.organizationId;
      if (!orgId) return [];
      usersQuery = query(collection(db, 'users'), where('organizationId', '==', orgId));
    }

    const usersSnapshot = await getDocs(usersQuery);
    return usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as AppUser));
  };

  // User management - Update user role
  const updateUserRole = async (userId: string, role: UserRole): Promise<void> => {
    if (!isAdmin && !isOrgAdmin) throw new Error('Only admins can update user roles');
    if (!isAdmin && role === 'admin') throw new Error('Only platform admins can assign admin role');

    await updateDoc(doc(db, 'users', userId), { role });
  };

  // User management - Delete user
  const deleteUser = async (userId: string): Promise<void> => {
    if (!isAdmin && !isOrgAdmin) throw new Error('Only admins can delete users');

    await deleteDoc(doc(db, 'users', userId));
  };

  // User management - Get pending users
  const getPendingUsers = async (organizationId?: string): Promise<AppUser[]> => {
    let pendingQuery;

    if (isAdmin && !organizationId) {
      // Admin can see all pending users
      pendingQuery = query(collection(db, 'pending_users'));
    } else {
      // Org admin can only see pending users in their organization
      const orgId = organizationId || user?.organizationId;
      if (!orgId) return [];
      pendingQuery = query(collection(db, 'pending_users'), where('organizationId', '==', orgId));
    }

    const pendingSnapshot = await getDocs(pendingQuery);
    return pendingSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as AppUser));
  };

  // User management - Delete pending user
  const deletePendingUser = async (userId: string): Promise<void> => {
    if (!isAdmin && !isOrgAdmin) throw new Error('Only admins can delete pending users');

    await deleteDoc(doc(db, 'pending_users', userId));
  };

  // Select organization (for admin or firm lead to switch between orgs)
  const selectOrganization = async (orgId: string): Promise<void> => {
    // Admins can select any org
    // Firm leads can select their firm's client orgs
    if (!isAdmin && !isFirmLead) {
      throw new Error('Only admins or firm leads can switch organizations');
    }

    const orgDocRef = doc(db, 'organizations', orgId);
    const orgDoc = await getDoc(orgDocRef);

    if (orgDoc.exists()) {
      const orgData = orgDoc.data();

      // Firm leads can only select orgs belonging to their firm
      if (isFirmLead && !isAdmin) {
        if (orgData.firmId !== firm?.id) {
          throw new Error('You can only access organizations belonging to your firm');
        }
      }

      setOrganization({ id: orgDoc.id, ...orgData } as Organization);
    }
  };

  // Update organization name (syncs with orgboard company name)
  const updateOrganizationName = async (name: string): Promise<void> => {
    if (!organization?.id) return;
    if (!canEdit) throw new Error('No permission to edit');

    await updateDoc(doc(db, 'organizations', organization.id), { name });

    // Update local state
    setOrganization(prev => prev ? { ...prev, name } : null);

    // Refresh organizations list to update dropdown
    if (isAdmin) {
      await loadOrganizations();
    }
  };

  // Update organization settings (logo, emoji, language)
  // orgId parameter allows updating a specific org without relying on context state
  const updateOrganizationSettings = async (settings: { logo?: string; emoji?: string; language?: 'he' | 'en' | 'ar'; name?: string }, orgId?: string): Promise<void> => {
    const targetOrgId = orgId || organization?.id;
    if (!targetOrgId) return;
    if (!canEdit) throw new Error('No permission to edit');

    const updateData: Record<string, any> = {};
    const localUpdateData: Record<string, any> = {};

    if (settings.name !== undefined && settings.name.trim()) {
      updateData.name = settings.name.trim();
      localUpdateData.name = settings.name.trim();
    }
    if (settings.logo !== undefined) {
      // Use deleteField() to clear, otherwise set the value
      updateData.logo = settings.logo === '' ? deleteField() : settings.logo;
      localUpdateData.logo = settings.logo === '' ? undefined : settings.logo;
    }
    if (settings.emoji !== undefined) {
      updateData.emoji = settings.emoji === '' ? deleteField() : settings.emoji;
      localUpdateData.emoji = settings.emoji === '' ? undefined : settings.emoji;
    }
    if (settings.language !== undefined) {
      updateData.language = settings.language;
      localUpdateData.language = settings.language;
    }

    if (Object.keys(updateData).length > 0) {
      updateData.updatedAt = serverTimestamp();
      await updateDoc(doc(db, 'organizations', targetOrgId), updateData);

      // Update local state if this is the current organization
      if (targetOrgId === organization?.id) {
        setOrganization(prev => prev ? { ...prev, ...localUpdateData } : null);
      }
    }
  };

  const value = {
    firebaseUser,
    user,
    organization,
    organizations,
    firm,
    isLoading,
    isAuthenticated,
    isAdmin,
    isFirmLead,
    isConsultant,
    isOrgAdmin,
    canEdit,
    signInWithEmail,
    signInWithGoogle,
    signInWithMicrosoft,
    signUpWithEmail,
    logout,
    createOrganization,
    getOrganizations,
    deleteOrganization,
    cloneOrganization,
    loadOrganizations,
    createUser,
    getUsers,
    getPendingUsers,
    updateUserRole,
    deleteUser,
    deletePendingUser,
    selectOrganization,
    updateOrganizationName,
    updateOrganizationSettings,
    canAccessOrg,
    getAccessibleOrganizations,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
